import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import MoneyInput from '../components/MoneyInput'
import type { CostBuildingRef } from '../types'

export interface NodeDialogState { kind: 'GROUP' | 'ITEM'; parentId: string | null; afterId?: string }

interface Props {
  state: NodeDialogState | null
  locked: boolean
  buildings: CostBuildingRef[]
  onClose: () => void
  onCreate: (input: Record<string, unknown>) => Promise<boolean>
}

const NONE = '__none__'

const NodeDialog: React.FC<Props> = ({ state, locked, buildings, onClose, onCreate }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [budget, setBudget] = useState<string | null>(null)
  const [budgetValid, setBudgetValid] = useState(true)
  const [buildingId, setBuildingId] = useState(NONE)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (state) { setName(''); setCode(''); setBudget(null); setBudgetValid(true); setBuildingId(NONE) }
  }, [state])

  const item = state?.kind === 'ITEM'
  const levelOne = state?.kind === 'GROUP' && state.parentId === null
  const title = t(item ? 'CostControl_new_item' : levelOne ? 'CostControl_new_level_one' : 'CostControl_new_group')
  const canSubmit = !!name.trim() && budgetValid && !submitting

  const submit = async () => {
    if (!state || !canSubmit) return
    const input: Record<string, unknown> = { kind: state.kind, name: name.trim(), parentId: state.parentId }
    if (state.afterId) input.afterId = state.afterId
    if (item && code.trim()) input.code = code.trim()
    if (item && !locked && budget) input.originalBudget = budget
    if (levelOne && buildingId !== NONE) input.buildingId = buildingId
    setSubmitting(true)
    const ok = await onCreate(input)
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open && !submitting) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit() }}>
          <div className="space-y-2">
            <Label htmlFor="cost-node-name">{t('CostControl_field_name')}</Label>
            <Input id="cost-node-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {item && (
            <div className="space-y-2">
              <Label htmlFor="cost-node-code">{t('CostControl_field_code')}</Label>
              <Input id="cost-node-code" value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
          )}
          {item && (locked ? (
            <p className="text-sm text-muted-foreground">{t('CostControl_locked_new_item_zero')}</p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="cost-node-budget">{t('CostControl_col_originalBudget')}</Label>
              <MoneyInput id="cost-node-budget" value={budget} onChange={(v, valid) => { setBudget(v); setBudgetValid(valid) }} />
            </div>
          ))}
          {levelOne && buildings.length > 0 && (
            <div className="space-y-2">
              <Label>{t('CostControl_field_building')}</Label>
              <Select value={buildingId} onValueChange={setBuildingId}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('CostControl_no_building')}</SelectItem>
                  {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('CostControl_create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default NodeDialog
